import { useCallback } from 'react'
import { toast } from 'react-toastify'
import { PlaylistItems } from '../../Model/PlaylistModel'
import { PriceTable } from '../../Model/PriceTableModel'
import { useBroadcastPlaylistUpdateMutation } from '../../service/DeviceSessionService'
import { useUpdatePriceTableLayoutsMutation } from '../../service/PriceTableLayoutService'
import { useFindAllTemplatesQuery } from '../../service/TemplateService'
import store from '../../store'
import { useAppSelector } from '../../store/hooks'
import { createPriceTableDisplays } from '../../Util/parseHTML'

/**
 * O HTML da tabela de preços exibido na TV é um "retrato" (price_table_layout)
 * gerado a partir dos produtos no momento em que a playlist foi salva. Quando
 * os produtos são editados depois (tela de Produtos ou import TXT), esse
 * retrato fica desatualizado até a playlist ser resalva manualmente.
 *
 * Este hook recalcula o retrato e reenvia para a TV de todas as playlists que
 * usam alguma das tabelas de preço informadas.
 */
export function useSyncPriceTables() {
  const user = useAppSelector(state => state.auth.user)
  const playlists = useAppSelector(state => state.playlist)
  const priceTables = useAppSelector(state => state.priceTable)
  const templates = useAppSelector(state => state.template)
  useFindAllTemplatesQuery(user?.id?.toString() || '', { skip: templates.length > 0 })
  const [updatePriceTableLayouts] = useUpdatePriceTableLayoutsMutation()
  const [broadcast] = useBroadcastPlaylistUpdateMutation()

  return useCallback(async (affectedPriceTableIds: Set<number>) => {
    if (affectedPriceTableIds.size === 0) return
    const products = store.getState().product.list
    let updated = 0

    for (const playlist of playlists) {
      if (!playlist.id) continue

      const items: PlaylistItems[] = playlist.items.map(item => {
        if (item.type !== 'priceTable') return item
        return {
          ...item,
          data: (item.data ?? priceTables.find(pt => pt.id === item._priceTableId)) as PriceTable,
          template: store.getState().template.find(t => t.id === item._templateId) ?? item.template,
        }
      })

      const tableItems = items.filter(item => item.type === 'priceTable')
      if (tableItems.length === 0) continue

      const hasAffected = tableItems.some(item => affectedPriceTableIds.has((item.data as PriceTable)?.id ?? -1))
      if (!hasAffected) continue

      // updatePriceTableLayouts substitui TODOS os layouts da playlist de uma vez.
      // Se algum item ainda não tem tabela/template resolvidos, pula essa playlist
      // para não apagar o layout das outras tabelas dela.
      const incomplete = tableItems.some(item => !(item.data as PriceTable)?.id || !item.template)
      if (incomplete) continue

      try {
        const layouts = createPriceTableDisplays(items, products)
        if (layouts.length > 0) {
          await updatePriceTableLayouts(
            layouts.map(l => ({ ...l, id: undefined, playlistId: playlist.id! }))
          ).unwrap()
        }
        await broadcast(playlist.id.toString()).unwrap()
        updated++
      } catch (e) {
        console.error(`[syncPriceTables] Falha ao sincronizar tabela de preços da playlist ${playlist.id}:`, e)
        toast.error(`Falha ao atualizar a TV da playlist "${playlist.name}". Abra e salve a playlist manualmente.`)
      }
    }

    if (updated > 0) {
      toast.success(`Tabela de preços enviada para a TV (${updated} playlist${updated > 1 ? 's' : ''})`)
    }
  }, [playlists, priceTables, templates, updatePriceTableLayouts, broadcast])
}
